import React, { useContext, useEffect, useState } from 'react';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import { getV31AvailableProducts } from '@/api/Products.js';
import { useRouter } from 'next/router';
import {
  addProduct,
  getBundleOfferDescription,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  getProductPropsWithCleanUrl,
  openListSideSheet,
  removeProduct,
  transformProduct,
} from '@/utils/products.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import useProductTags from '@/hooks/useProductTags.js';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary.js';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar.js';
import { productsForOrder } from '@/store/cart.js';
import {
  BestSellerSection,
  BrandInfo,
  OffersSnackbar,
  ProductAisleV2,
  ProductCatalogueV2,
  SnackBar,
} from '@desquared/wave-components-library';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage.js';
import styles from './BrandProductSections.module.css';
import { useTranslations } from 'next-intl';
import { CurrencyContext } from '@/contexts/CurrencyProvider.jsx';
import Link from 'next/link.js';
import { checkListStatus } from '@/utils/lists.js';
import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { aislesViewAnalyticsFunction, getProductInfo, selectItem } from '@/utils/analytics.js';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold.js';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { useQuery } from '@tanstack/react-query';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { useIsRtl } from '@/contexts/DirectionContext.jsx';

export function BrandProductSections({ brandSections }) {
  const [sections, setSections] = useState([]);
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const router = useRouter();
  const [showSnackBar, setShowSnackbar] = useState(false);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [productsOrder, setProductsOrder] = useState();
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const defaultProductImage = useGetDefaultProductImage();
  const t = useTranslations();
  const { formatter } = useContext(CurrencyContext);
  const isMobile = useWindowUnderThreshold(850);
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const a = useTranslations('common.asides');
  const isRtl = useIsRtl();

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);

    return () => {
      productsForOrder$.unsubscribe();
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const { data: fetchedProductSections, isLoading } = useQuery({
    queryKey: ['brandSections', brandSections, router.locale],
    queryFn: async () => {
      const selectedCollectionTypes = getCollectionTypes();

      if (!brandSections?.length) return [];

      const results = await Promise.all(
        brandSections.map(section => {
          const skus = section?.content?.brand_highlighted_products_section_products_skus;
          return getV31AvailableProducts(skus, selectedCollectionTypes, router.locale);
        })
      );

      return results;
    },
    enabled: !!brandSections?.length,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!brandSections || !fetchedProductSections) return;

    const finalBrandSections = brandSections.map((brandSection, i) => {
      const brandTitle = brandSection?.content?.brand_highlighted_products_section_title;
      const brandSubtitle = brandSection?.content?.brand_highlighted_products_section_subtitle;
      const brandVideoUrl = brandSection?.content?.brand_highlighted_products_section_video_url;
      const brandSkus = brandSection?.content?.brand_highlighted_products_section_products_skus;
      const brandSpecial = i === 0; // temporary variable
      const productSection = fetchedProductSections[i];

      const brandProducts =
        productSection?.length > 0
          ? productSection
              .map(product =>
                transformProduct(
                  product,
                  setShowSnackbar,
                  undefined,
                  productTags,
                  bundleOffersSummary,
                  undefined,
                  undefined,
                  undefined,
                  personalizedProductsIds,
                  a
                )
              )
              .map(p => ({ ...p, categoryIds: p.categories, _id: p.id }))
              .map(enhanceProductWithSlug)
              .map(p => ({ ...p, slug: p.slug.slice(1) }))
          : [];

      const sortedArray = brandSkus
        .map(sku => brandProducts.find(product => product.sku === sku))
        .filter(product => product !== undefined);

      return {
        brandTitle,
        brandSubtitle,
        brandVideoUrl,
        brandSpecial,
        brandProducts: sortedArray,
      };
    });
    setSections(finalBrandSections);
  }, [productTags, bundleOffersSummary, productsOrder, fetchedProductSections]);

  if (isLoading)
    return (
      <div className={styles.emptyProductsContainer}>
        <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} />
      </div>
    );

  if (sections.length === 0) return;

  return (
    <div className={styles.brandProductSectionsContainer}>
      {sections.map((section, i) => {
        if (section.brandSpecial) {
          return (
            <BestSellerSection
              key={i}
              products={section.brandProducts.slice(0, 3)} //Max 3 bestsellers will be shown
              list={'best_seller_products_aisle'}
              analyticsFunction={(products, list) => aislesViewAnalyticsFunction(products, list)}
              brandTitle={section.brandTitle}
              brandSubtitle={section.brandSubtitle}
              brandVideoUrl={section.brandVideoUrl}
              isBestSeller={true}
              ctaIcon={process.env.ctaIcon}
              listIcon={process.env.listIcon}
              offerLabelHasRibbon={process.env.offerLabelHasRibbon}
              isTileDetailsAnimated={process.env.isTileDetailsAnimated}
              defaultQsText={t('common.qs.default')}
              defaultQsUnavailableText={t('common.qs.default_unavailable')}
              defaultProductImage={defaultProductImage}
              currencyFormatter={formatter}
              // onProductDetails={} //Is it needed?
              checkStatus={checkListStatus}
              onListClick={openListSideSheet}
              addProduct={product =>
                addProduct(
                  product,
                  false,
                  {
                    item_list_id: 'bestsellerProducts',
                    item_list_name: 'bestsellerProducts',
                  },
                  false,
                  t('common.asides.side_cart.no_comment')
                )
              }
              ImageComponent={CustomImage}
              removeProduct={product =>
                removeProduct(
                  product,
                  {
                    item_list_id: 'bestsellerProducts',
                    item_list_name: 'bestsellerProducts',
                  },
                  t('common.asides.side_cart.no_comment')
                )
              }
              LinkComponent={props => {
                const propsWithCleanUrl = getProductPropsWithCleanUrl(props);
                return (
                  <Link
                    prefetch={false}
                    {...propsWithCleanUrl}
                    href={'/' + propsWithCleanUrl.href}
                    onClick={() => {
                      const product = getProductBasedOnSlug(section.brandProducts, propsWithCleanUrl.href);
                      if (product) {
                        selectItem(getProductInfo(product?.originalValues), {
                          item_list_id: 'bestsellerProductsAisle',
                          item_list_name: 'bestsellerProductsAisle',
                          index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                        });
                      }
                    }}
                  />
                );
              }}
              forYouLabel={t('common.user.profile.menu.for_you')}
            />
          );
        } else {
          return (
            <div key={i} className={styles.highlightedProductsSection}>
              <BrandInfo
                brandTitle={section.brandTitle}
                brandSubtitle={section.brandSubtitle}
                brandVideoUrl={section.brandVideoUrl}
              />
              <ProductAisleV2
                products={section.brandProducts}
                tilesSize={'default'}
                isMobile={isMobile}
                hasPadding={true}
                horizontal={true}
                onListClick={openListSideSheet}
                checkStatus={checkListStatus}
                addProduct={product =>
                  addProduct(
                    product,
                    false,
                    {
                      item_list_id: 'highlightedProducts',
                      item_list_name: 'highlightedProducts',
                    },
                    false,
                    t('asides.side_cart.no_comment')
                  )
                }
                removeProduct={product =>
                  removeProduct(
                    product,
                    {
                      item_list_id: 'highlightedProducts',
                      item_list_name: 'highlightedProducts',
                    },
                    t('asides.side_cart.no_comment')
                  )
                }
                showMore={false}
                hideLastTile={true}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
                list={'highlighted_products_aisle'}
                analyticsFunction={(products, list) => aislesViewAnalyticsFunction(products, list)}
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);
                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      href={propsWithCleanUrl.href}
                      onClick={() => {
                        // we add slice because from ProductTile slug is '/ + slug'. And newProducts dont have leading slash
                        const product = getProductBasedOnSlug(section.brandProducts, propsWithCleanUrl.href.slice(1));

                        if (product) {
                          selectItem(getProductInfo(product?.originalValues), {
                            item_list_id: 'highlightedProductsAisle',
                            item_list_name: 'highlightedProductsAisle',
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                setMaxSnackBar={setShowSnackbar}
                currencyFormatter={formatter}
                defaultProductImage={defaultProductImage}
                forYouLabel={t('common.user.profile.menu.for_you')}
                isRtl={isRtl}
              />
            </div>
          );
        }
      })}
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('common.snackbar')} position={'center'} />
      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('common.offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        hasShadow={true}
      />
    </div>
  );
}
